import React, { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  className?: string;
  showPasswordToggle?: boolean;
}

/**
 * Componente Input reutilizable con diseño moderno y elegante
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((
  { label, error, icon, className = '', showPasswordToggle = false, type, ...props },
  ref
) => {
  const [showPassword, setShowPassword] = useState(false);
  
  // Si es un input de contraseña con toggle, manejamos el tipo internamente
  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password') 
    : type;

  const inputClasses = `
    w-full px-4 py-3 
    bg-secondary-700 border-0
    rounded-lg text-secondary-50 placeholder-secondary-400
    focus:outline-none focus:ring-4 focus:ring-primary-500
    transition-all duration-200 ease-in-out
    disabled:opacity-50 disabled:cursor-not-allowed
    text-base
    hover:bg-secondary-600
    ${error ? 'focus:ring-red-500' : ''}
    ${icon ? 'pl-10' : ''}
    ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}
    ${className}
  `;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-secondary-50 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-secondary-400">
            {icon}
          </div>
        )}
        <input
          ref={ref}
          type={inputType}
          className={inputClasses}
          {...props}
        />
        {showPasswordToggle && type === 'password' && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-primary-500 transition-colors"
            onClick={() => setShowPassword(!showPassword)}
            disabled={props.disabled}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-400 font-medium">{error}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
export default Input;